import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Text,
  VStack,
  Link,
  Popover,
  PopoverTrigger,
  Flex,
  IconButton,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  HStack,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AiOutlineUserAdd } from 'react-icons/ai'
import { BsInfoCircleFill } from 'react-icons/bs'
import { FaUserEdit } from 'react-icons/fa'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { IKReactSelect, colors } from '../../../ui-kit'
import { FeaturesEnum } from '../../../utils/features'
import useToast from '../../hooks/useToast'
import type {
  AddUserResponse,
  UserErrorResponse,
  UserGroupResponse,
  UserSessionResponse,
} from '../../interfaces'

interface Props {
  isEdit: boolean
  userName?: string
  userEmail?: string
  roles?: string
  id?: string
  user: UserSessionResponse | null
  groupData?: UserGroupResponse[] | undefined
  isOpen: boolean
  onClose(): void
  getData(): Promise<void>
  getGroupData(): Promise<void>
}

interface FormProps {
  userName: string
  userEmail: string
  roles: string
}
interface SelectedGroupType {
  label: string
  value: string
}

const AddEditUserModal = ({
  isEdit,
  userName,
  userEmail,
  roles,
  id,
  user,
  groupData,
  isOpen,
  onClose,
  getData,
  getGroupData,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [formError, setFormError] = useState<string>('')
  const [displayGroups, setDisplayGroups] = useState<boolean>(false)
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroupType[]>()
  const toast = useToast()
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const initialValues = isEdit
    ? { userName, userEmail, roles }
    : { userName: '', userEmail: '', roles: '' }

  const shouldDisplayGroups = useCallback(
    role => {
      if (groupData?.length && role && role === '10') setDisplayGroups(true)
      else setDisplayGroups(false)
    },
    [groupData],
  )

  useEffect(() => {
    shouldDisplayGroups(roles)
  }, [groupData, roles])

  useEffect(() => {
    setDisplayGroups(false)
    setSelectedGroups(undefined)
    if (id && isEdit && roles) {
      setSelectedGroups(
        groupData
          ?.filter(value => value.userIds.includes(id))
          ?.map(data => ({ label: data.label, value: data.groupId })),
      )
      shouldDisplayGroups(roles)
    }
  }, [groupData, id, isOpen, isEdit, roles])

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setFormError('')
      }}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <Formik
          initialValues={initialValues}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber && isEdit) {
              const updateObject = {
                id,
                userName: values.userName,
                userEmail: values.userEmail,
                roles: values.roles,
              }
              if (values.roles === '10')
                updateObject['groupIds'] = selectedGroups?.map(gp => gp?.value)
              const response = (await request.put(
                `/api/clients/${user.clientNumber}/users/${id}`,
                updateObject,
              )) as null | UserErrorResponse

              if (request.error) {
                setFormError(
                  response?.message ??
                    t('something_went_wrong', 'Something went wrong'),
                )
                const modalBody = document.getElementById('scroll')
                modalBody?.scrollTo({ top: 0, behavior: 'smooth' })
              } else {
                toast({
                  description: t(
                    'successfully_updated',
                    'Successfully updated',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                await getData()
                await getGroupData()
                onClose()
              }
            } else if (user?.clientNumber) {
              const updateObject = {
                userName: values.userName,
                userEmail: values.userEmail,
                roles: values.roles,
              }
              if (values.roles === '10')
                updateObject['groupIds'] = selectedGroups?.map(gp => gp?.value)
              const response = (await request.post(
                `/api/clients/${user.clientNumber}/users`,
                updateObject,
              )) as Partial<UserErrorResponse & AddUserResponse>

              if (request.error) {
                setFormError(
                  response?.message ??
                    t('something_went_wrong', 'Something went wrong'),
                )
                const modalBody = document.getElementById('scroll')
                modalBody?.scrollTo({ top: 0, behavior: 'smooth' })
              } else {
                toast({
                  description: t('successfully_added', 'Successfully added'),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                setFormError('')
                onClose()
                await getData()
                await getGroupData()
              }
            }
          }}
          validationSchema={Yup.object().shape({
            userName: Yup.string().required('Required'),
            userEmail: Yup.string()
              .email('Must be a valid email')
              .required('Required'),
            roles: Yup.mixed()
              .oneOf(
                ['2', '6', '7', '8', '9', '10'],
                'Must be a valid user role',
              )
              .required('Required'),
          })}
        >
          {({ isSubmitting, submitForm }) => (
            <>
              <ModalHeader>
                <Icon
                  as={isEdit ? FaUserEdit : AiOutlineUserAdd}
                  color={colors.appBattleshipGrey}
                  backgroundColor={colors.appPaleGrey}
                  boxSize={6}
                  padding="0.1em"
                  borderRadius="50%"
                  marginRight="0.5em"
                />
                {isEdit
                  ? t('edit_user', 'Edit User')
                  : t('add_user', 'Add User')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody id="scroll">
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Field name="userName">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      isInvalid={Boolean(
                        form.errors.userName && form.touched.userName,
                      )}
                      isRequired
                    >
                      <FormLabel>{t('name', 'Name')}</FormLabel>
                      <Text
                        color={colors.appBattleshipGrey}
                        marginBottom="0.5em"
                      >
                        {t(
                          'name_of_user_associated',
                          'Name of the user that will be associated with this account.',
                        )}
                      </Text>
                      <Input
                        {...field}
                        placeholder={t('name_of_the_user', 'Name of the user')}
                      />
                      {form.errors.userName && form.touched.userName ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userName}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="userEmail">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.userEmail && form.touched.userEmail,
                      )}
                      isReadOnly={isEdit}
                      isDisabled={isEdit}
                      isRequired
                    >
                      <FormLabel>{t('user_email', 'User Email')}</FormLabel>
                      <Text
                        color={colors.appBattleshipGrey}
                        marginBottom="0.5em"
                      >
                        {t(
                          'email_id_associated',
                          'Email Id of the user that will be associated with this account.',
                        )}
                      </Text>
                      <Input
                        {...field}
                        placeholder={t('email_id', 'Email Id')}
                      />
                      {form.errors.userEmail && form.touched.userEmail ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.userEmail}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                <Field name="roles">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      marginTop="0.3em"
                      isInvalid={Boolean(
                        form.errors.roles && form.touched.roles,
                      )}
                      isRequired
                    >
                      <FormLabel>{t('user_role', 'User Role')}</FormLabel>
                      <Flex>
                        <Text
                          color={colors.appBattleshipGrey}
                          marginBottom="0.5em"
                        >
                          {t(
                            'select_user_role',
                            "Select the role that best suits user's profile.",
                          )}
                        </Text>
                        <Popover trigger="hover" placement="top">
                          <PopoverTrigger>
                            <Flex mb={2}>
                              <IconButton
                                aria-label="Info icon"
                                icon={<BsInfoCircleFill />}
                                color={colors.appBlueyGrey}
                                size="sm"
                                variant="link"
                              />
                            </Flex>
                          </PopoverTrigger>
                          <PopoverContent>
                            <PopoverArrow />
                            <PopoverBody>
                              <Trans t={t} i18nKey="user_roles_info">
                                <Text>
                                  Learn more about different user roles{' '}
                                  <Link
                                    isExternal
                                    lineHeight={1.2}
                                    href="https://docs.imagekit.io/features/user-access-management#user-roles"
                                  >
                                    here
                                  </Link>
                                  .
                                </Text>
                              </Trans>
                            </PopoverBody>
                          </PopoverContent>
                        </Popover>
                      </Flex>
                      <RadioGroup {...field} value={form.values.roles}>
                        <VStack
                          spacing="1em"
                          direction="column"
                          alignItems="flex-start"
                        >
                          <Box>
                            <Radio
                              value="2"
                              onChange={value => {
                                form.handleChange(value)
                                shouldDisplayGroups('2')
                              }}
                            >
                              {t(
                                'account_administrator',
                                'Account Administrator',
                              )}
                            </Radio>
                            <Text
                              color={colors.appBattleshipGrey}
                              paddingLeft="1.5em"
                            >
                              {t(
                                'account_administrator_description',
                                'Full access to everything including user management',
                              )}
                            </Text>
                          </Box>
                          <Box>
                            <Radio
                              value="6"
                              onChange={value => {
                                form.handleChange(value)
                                shouldDisplayGroups('6')
                              }}
                            >
                              {t('developer', 'Developer')}
                            </Radio>
                            <Text
                              color={colors.appBattleshipGrey}
                              paddingLeft="1.5em"
                            >
                              {t(
                                'developer_description',
                                'Access to everything except user management and billing',
                              )}
                            </Text>
                          </Box>
                          <Box>
                            <Radio
                              value="8"
                              onChange={value => {
                                form.handleChange(value)
                                shouldDisplayGroups('8')
                              }}
                            >
                              {t(
                                'media_library_full_access',
                                'Media Library Full Access',
                              )}
                            </Radio>
                            <Text
                              color={colors.appBattleshipGrey}
                              paddingLeft="1.5em"
                            >
                              {t(
                                'media_library_full_access_description',
                                'Full access to media-library',
                              )}
                            </Text>
                          </Box>
                          <Box>
                            <Radio
                              value="9"
                              onChange={value => {
                                form.handleChange(value)
                                shouldDisplayGroups('9')
                              }}
                            >
                              {t(
                                'media_library_view_only_access',
                                'Media Library View-Only Access',
                              )}
                            </Radio>
                            <Text
                              color={colors.appBattleshipGrey}
                              paddingLeft="1.5em"
                            >
                              {t(
                                'media_library_view_only_access_description',
                                'View only access to media library',
                              )}
                            </Text>
                          </Box>
                          <HStack spacing="24px">
                            <Box>
                              <Radio
                                value="10"
                                isDisabled={
                                  !user?.pricingPlan.featuresList.includes(
                                    FeaturesEnum.ACL_AND_COLLECTIONS,
                                  )
                                }
                                onChange={value => {
                                  form.handleChange(value)
                                  shouldDisplayGroups('10')
                                }}
                              >
                                {t(
                                  'media_library_restricted_access',
                                  'Media library restricted access',
                                )}
                              </Radio>
                              <Text
                                color={colors.appBattleshipGrey}
                                paddingLeft="1.5em"
                              >
                                {t(
                                  'media_library_restricted_access_description',
                                  'Restricted access to media library',
                                )}
                              </Text>
                            </Box>
                            {!user?.pricingPlan.featuresList.includes(
                              FeaturesEnum.ACL_AND_COLLECTIONS,
                            ) ? (
                              <Popover trigger="hover">
                                <PopoverTrigger>
                                  <Flex>
                                    <IconButton
                                      aria-label="Info icon"
                                      icon={<BsInfoCircleFill />}
                                      color={colors.appBlueyGrey}
                                      size="sm"
                                      variant="link"
                                    />
                                  </Flex>
                                </PopoverTrigger>
                                <PopoverContent>
                                  <PopoverArrow />
                                  <PopoverBody>
                                    <Trans t={t} i18nKey="acl_plan_info">
                                      <Text>
                                        Collaboration and sharing feature is not
                                        available in your current plan.{' '}
                                        <Link
                                          isExternal
                                          lineHeight={1.2}
                                          href="https://docs.imagekit.io/features/user-access-management#media-library-restricted-access"
                                        >
                                          {t('learn_more', 'Learn more')}
                                        </Link>
                                        .
                                        <br />
                                        <Link
                                          isExternal
                                          href="https://imagekit.io/contact-us/"
                                        >
                                          Contact sales
                                        </Link>{' '}
                                        to enable this feature.
                                      </Text>
                                    </Trans>
                                  </PopoverBody>
                                </PopoverContent>
                              </Popover>
                            ) : null}
                          </HStack>
                          <Box>
                            <Radio
                              value="7"
                              onChange={value => {
                                form.handleChange(value)
                                shouldDisplayGroups('7')
                              }}
                            >
                              {t('finance', 'Finance')}
                            </Radio>
                            <Text
                              color={colors.appBattleshipGrey}
                              paddingLeft="1.5em"
                            >
                              {t(
                                'finance_description',
                                'Access to analytics and billing',
                              )}
                            </Text>
                          </Box>
                        </VStack>
                      </RadioGroup>
                      {form.errors.roles && form.touched.roles ? (
                        <FormErrorMessage mt="0.25em">
                          {form.errors.roles}
                        </FormErrorMessage>
                      ) : (
                        <Box height="1.55em" />
                      )}
                    </FormControl>
                  )}
                </Field>
                {displayGroups && (
                  <Field name="groups">
                    {({ field, form }: FieldProps<string, FormProps>) => (
                      <FormControl>
                        <FormLabel>{t('group', 'Group')}</FormLabel>
                        <Text
                          color={colors.appBattleshipGrey}
                          marginBottom="0.5em"
                        >
                          {t(
                            'add_user_to_groups',
                            'Add user to relevant user groups',
                          )}
                        </Text>
                        <IKReactSelect
                          menuPlacement="auto"
                          variant="compact"
                          placeholder={t('select_groups', 'Select groups')}
                          options={groupData?.map(data => ({
                            label: data.label,
                            value: data.groupId,
                          }))}
                          value={selectedGroups}
                          onChange={group => {
                            setSelectedGroups(group)
                          }}
                          isMulti
                          isClearable
                          menuPosition="fixed"
                          isDisabled={!displayGroups}
                          menuShouldScrollIntoView
                        />
                      </FormControl>
                    )}
                  </Field>
                )}
              </ModalBody>
              <ModalFooter
                borderTop={`1px solid ${colors.appGrey}`}
                style={{ gap: '1em' }}
              >
                <Button onClick={onClose} variant="outline">
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default AddEditUserModal
